import axios from 'axios';

// Create an instance of axios with default configuration
const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'http://localhost:5000',
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add a request interceptor to inject the auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add a response interceptor to handle token expiration
api.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    const originalRequest = error.config;
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      
      // Redirect to login page
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth services
export const login = async (credentials) => {
  return api.post('/api/auth/login', credentials);
};

export const register = async (userData) => {
  return api.post('/api/auth/register', userData);
};

export const logout = () => {
  localStorage.removeItem('token');
  localStorage.removeItem('user');
  window.location.href = '/';
};

// News services
export const fetchNews = async () => {
  return api.get('/api/news');
};

// Contact services
export const submitContactForm = async (formData) => {
  return api.post('/api/contact', formData);
};

// Admin settings services
export const adminServices = {
  getSettings: () => api.get('/api/admin/settings'),
  updateSettings: (settings) => api.post('/api/admin/settings', settings),
  updatePassword: (currentPassword, newPassword) => api.post('/api/admin/settings/password', {
    currentPassword,
    newPassword
  })
};

// Email services
export const emailServices = {
  // Email folders
  getFolders: () => api.get('/api/emails/folders'),
  createFolder: (name) => api.post('/api/emails/folders', { name }),
  deleteFolder: (id) => api.delete(`/api/emails/folders/${id}`),
  
  // Emails
  getEmails: (folderId, page = 1, limit = 20) => 
    api.get(`/api/emails/list?folder_id=${folderId}&page=${page}&limit=${limit}`),
  getEmail: (id) => api.get(`/api/emails/view/${id}`),
  sendEmail: (emailData) => api.post('/api/emails/send', emailData),
  
  // Email actions
  markAsRead: (emailIds) => api.post('/api/emails/actions', { action: 'mark_read', email_ids: emailIds }),
  markAsUnread: (emailIds) => api.post('/api/emails/actions', { action: 'mark_unread', email_ids: emailIds }),
  moveEmails: (emailIds, folderId) => api.post('/api/emails/actions', { 
    action: 'move', 
    email_ids: emailIds,
    folder_id: folderId
  }),
  deleteEmails: (emailIds, permanent = false) => api.post('/api/emails/actions', { 
    action: 'delete', 
    email_ids: emailIds,
    permanent
  }),
  
  // Drafts
  getDrafts: (page = 1, limit = 20) => api.get(`/api/emails/drafts?page=${page}&limit=${limit}`),
  getDraft: (id) => api.get(`/api/emails/drafts/${id}`),
  saveDraft: (draftData) => api.post('/api/emails/drafts', draftData),
  deleteDraft: (id) => api.delete(`/api/emails/drafts/${id}`),
  
  // Email fetch and search
  fetchEmails: (limit = 20) => api.post('/api/emails/fetch', { limit }),
  searchEmails: (query, folder = null, page = 1, limit = 20) => {
    let url = `/api/emails/search?q=${encodeURIComponent(query)}&page=${page}&limit=${limit}`;
    if (folder) url += `&folder=${folder}`;
    return api.get(url);
  }
};

// User services
export const userServices = {
  getCurrentUser: () => api.get('/api/users/profile'),
  updateProfile: (userData) => api.put('/api/users/profile', userData),
  getAllUsers: () => api.get('/api/users'), // Admin only
  getUser: (id) => api.get(`/api/users/${id}`), // Admin only
  updateUser: (id, userData) => api.put(`/api/users/${id}`, userData), // Admin only
  deleteUser: (id) => api.delete(`/api/users/${id}`) // Admin only
};

export default api; 